import { useEffect } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchIssues } from "../lib/api";
import { canSubmit, useIssueStore } from "../lib/issue-store";
import { useNotify } from "../lib/notify-store";
import { roleCan, useRole } from "../lib/roles";
import type { IssueDetail } from "../lib/segment-types";
import { NewsletterEditor } from "../components/NewsletterEditor";
import { PageSkeleton } from "../components/PageSkeleton";
import { Surface } from "../components/primitives";

export function NewsletterEditorPage() {
  const { issueId } = useParams<{ issueId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const seed = useQuery({ queryKey: ["issues"], queryFn: fetchIssues });
  const store = useIssueStore(seed.isError ? [] : seed.data);
  const notify = useNotify();
  const { role } = useRole();
  const fromState = (location.state as { issue?: IssueDetail } | null)?.issue;
  const issue = store.issues?.find((i) => i.id === issueId) ?? (fromState?.id === issueId ? fromState : null);

  // A draft handed over in router state has to reach the store, or the editor
  // renders a copy nothing can write to and every edit disappears in silence.
  useEffect(() => {
    if (!store.ready || !issueId) return;
    const inStore = store.issues?.some((i) => i.id === issueId);
    if (!inStore && fromState?.id === issueId) store.upsert(fromState);
  }, [store, issueId, fromState]);

  useEffect(() => {
    if (!store.ready) return;
    if (!issue) navigate("/content", { replace: true });
  }, [store.ready, issue, navigate]);

  if (!store.ready || !issue) return <PageSkeleton />;

  return (
    <NewsletterEditor
      sync={store.sync}
      issue={issue}
      canSubmit={canSubmit(issue)}
      canApprove={roleCan(role, "newsletters.approve")}
      onBack={() => navigate("/content")}
      onChange={(fields) => store.updateFields(issue.id, fields)}
      onResolveComment={(commentId) => store.resolveComment(issue.id, commentId)}
      onRestoreVersion={(versionId) => store.restoreVersion(issue.id, versionId)}
      onReopen={() => store.requestChanges(issue.id, "")}
      onRequestChanges={(comment, blockId) => {
        store.requestChanges(issue.id, comment, blockId);
        notify.push({
          kind: "changes",
          title: `Changes requested on “${issue.title}”`,
          body: comment.trim() || "Please revise this draft and resubmit.",
          href: `/content/${issue.id}`,
          forRoles: ["freelancer", "admin"],
        });
        navigate("/content");
      }}
      onSubmit={() => {
        store.submitForReview(issue.id);
        notify.push({
          kind: "submitted",
          title: "Submitted for review",
          body: `“${issue.title}” is waiting for an editor.`,
          href: "/content",
          forRoles: ["editor", "admin"],
        });
        navigate("/content");
      }}
    />
  );
}

export function AccessDenied({ need }: { need: string }) {
  return (
    <Surface>
      <div style={{ padding: "var(--ui-space-8)", textAlign: "center" }}>
        <div style={{ fontSize: "var(--ui-text-section)", fontWeight: 600 }}>This page is for {need}</div>
        <p style={{ color: "var(--c-t3)", fontSize: "var(--ui-text-control)" }}>
          You do not have access with this account.
        </p>
        <Link to="/content" style={{ color: "var(--c-accent)", fontWeight: 600 }}>Open newsletters</Link>
      </div>
    </Surface>
  );
}

/**
 * An address that matches no page used to render the shell with an empty
 * middle — no message, no way back, and nothing to say whether the page had
 * moved or never existed.
 */
export function NotFound() {
  const { pathname } = useLocation();
  return (
    <Surface>
      <div style={{ padding: "var(--ui-space-8)", textAlign: "center" }}>
        <div style={{ fontSize: "var(--ui-text-section)", fontWeight: 600 }}>This page does not exist</div>
        <p style={{ color: "var(--c-t3)", fontSize: "var(--ui-text-control)" }}>
          Nothing is published at <code>{pathname}</code>. It may have been renamed,
          or the newsletter it pointed at may have been deleted.
        </p>
        <Link to="/content" style={{ color: "var(--c-accent)", fontWeight: 600 }}>Open newsletters</Link>
      </div>
    </Surface>
  );
}
